import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Image, Video, Type, Layout, Users, Mail, Upload, Eye,
  Trash2, Plus, Save, EyeOff, Link2, Loader2, RotateCcw,
  Minus, GripVertical, Grid3X3, BookOpen,
  Check, X as XIcon, CalendarDays, ClipboardList,
  ExternalLink, Play, Square, Circle, RectangleHorizontal,
  AlignLeft, AlignCenter, AlignRight, Maximize2, Heart, ChevronUp, ChevronDown,
  ArrowUp, ArrowDown, Settings2, Palette, Move, Pencil, Monitor, Smartphone, FolderOpen,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { GlassCard, ButtonsEditor, SortableBlock } from './content-editor/Primitives';
import {
  HeroEditor, BannerEditor, AboutEditor, GalleryBlockEditor,
  SignupEditor, CustomTextEditor, CustomImageEditor, VideoEditor,
  TestimonialEditor, SpacerEditor, BookingEditor, DonationsEditor,
  FeaturedProductsEditor, FormBlockEditor, EmbedBlockEditor, SingleImageBlockEditor,
  MarqueeEditor
} from './content-editor/BlockEditors';
import UniversalContentEditor from './content-editor/UniversalContentEditor';
import { blockLabels, blockIcons, BLOCK_TYPES, BLOCK_DEFAULTS } from './content-editor/blockTypes';
import {
  BlockStyleControls, InlineText, ImageUploadZone, AddBlockInsertion,
  BlockToolbar, MediaPickerModal, FullBlock,
  autoFontColor, loadFont,
  BlockEditContext, StableInlineE,
} from './content-editor/DashboardParts';
import { AddSectionPill } from './sqs/SectionHoverOverlay';
import EditDrawer from './sqs/EditDrawer';
import PublishBar from './sqs/PublishBar';
import LeftRailPagesTree from './sqs/LeftRailPagesTree';
import RightRailInspector from './sqs/RightRailInspector';
import ColumnRuler from './sqs/ColumnRuler';
import FloatingImageToolbar from './sqs/imageFrameSystem/FloatingImageToolbar';
import { BuilderDeviceContext } from './sqs/imageFrameSystem/DeviceContext';
import TextToolbar from './sqs/textEditor/TextToolbar';
import FluidOverlayLayer from './sqs/FluidOverlayLayer';
import AddSectionModal from './sqs/AddSectionModal';
import AddBlockMenu from './sqs/AddBlockMenu';
import SectionColorPanel from './sqs/SectionColorPanel';
import LayoutsPanel from './sqs/LayoutsPanel';
import ImageEditorPanel from './sqs/ImageEditorPanel';
import { CanvasHeader, CanvasFooter } from './sqs/CanvasChrome';
import AIGenerateModal from './sqs/AIGenerateModal';
import { layoutStyleVars } from './sqs/layoutEngine';
import { EditableHoverBoundary, InlineSelectionToolbar } from './sqs/InlineEditing';
import { FluidDragHandle } from './sqs/useFluidTextDrag';
import StyleButtons from './sqs/StyleButtons';
import { renderSiteBlock } from './shared/renderSiteBlock';
import { resolveSiteTokens, SiteSurface } from './shared/siteTokens';
import useContentEditor from './content-editor/useContentEditor';
import InlineButtonEditor from './sqs/InlineButtonEditor';
import { useNavigate, useLocation } from 'react-router-dom';
import { Undo2, Redo2, Sparkles, History, PanelLeft, PanelRight } from 'lucide-react';
import HistoryPanel from './sqs/HistoryPanel';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function DashboardContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const editor = useContentEditor();
  const {
    canvasRef,
    fluidGrid,
    blocks, setBlocks, allProducts, loading, saving,
    hasChanges,
    editPanelBlock, setEditPanelBlock,
    showMediaPicker, setShowMediaPicker, mediaPickerTarget,
    viewMode, setViewMode,
    pages, activePage,
    showNewPage, setShowNewPage,
    newPageTitle, setNewPageTitle,
    editingPageTitle, setEditingPageTitle,
    addSectionOpen, setAddSectionOpen,
    addSectionAfter, setAddSectionAfter,
    colorPanelBlock, setColorPanelBlock,
    layoutsPanelBlock, setLayoutsPanelBlock,
    siteTheme, setSiteTheme, siteMeta,
    addBlockTarget, setAddBlockTarget,
    imageEditorBlock, setImageEditorBlock,
    aiModalOpen, setAiModalOpen,
    aiMode, setAiMode,
    aiInsertAfter, setAiInsertAfter,
    palette, headers,
    inlineBtn,
    fluidDrag,
    multiSelect,
    device, setDevice,
    handleSave,
    switchToPage, handleCreatePage, handleDeletePage, handleRenamePageSave,
    updateBlock, updateBlockContent,
    setBlockLayout, toggleVisibility, deleteBlock, moveBlock,
    addBlockAt, insertBlocksFromTemplate, insertAiBlocks,
    duplicateBlock, favoriteBlock,
    setSectionHeight, setSectionAttached,
    applyLayout,
    openMediaPicker, handleMediaSelect,
    undo, redo, canUndo, canRedo,
    jumpTo, historyList, historyIndexNow,
  } = editor;

  // ── Iter 190d — AUTOSAVE on route/navigation away.
  // When the user clicks a different left-sidebar tab (Pages, Templates,
  // Styles, Assets, SEO/AIO, Custom CSS, etc.), React Router unmounts
  // DashboardContent. Persist any unsaved edits BEFORE the component
  // dies so the user never loses work. We use refs so the cleanup
  // closure always sees the latest values (state changes never re-run
  // the mount-only effect).
  const hasChangesRef = useRef(hasChanges);
  const handleSaveRef = useRef(handleSave);
  useEffect(() => { hasChangesRef.current = hasChanges; }, [hasChanges]);
  useEffect(() => { handleSaveRef.current = handleSave; }, [handleSave]);
  useEffect(() => {
    return () => {
      if (hasChangesRef.current) {
        try { handleSaveRef.current(); } catch (_) { /* fire-and-forget */ }
      }
    };
  }, []);

  // ── Iter 150 — section "selection" mode (click-to-select).
  // Click anywhere on a section (that isn't an inline-edit target) marks
  // it as selected → the SectionControlCard (white floating card) sticks
  // around until the user clicks outside any section or presses Escape.
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  // Iter 198d — Persistent rails. Collapsed state persisted to localStorage.
  // Iter 198k — Default to COLLAPSED so the canvas gets full desktop width
  // out of the box (matches Squarespace and the /pages preview). Vendors
  // can expand either rail on demand via the slim toggle.
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem('wb:leftRailCollapsed');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const [rightRailCollapsed, setRightRailCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem('wb:rightRailCollapsed');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('wb:leftRailCollapsed', leftRailCollapsed ? '1' : '0'); } catch { /* noop */ }
  }, [leftRailCollapsed]);
  useEffect(() => {
    try { localStorage.setItem('wb:rightRailCollapsed', rightRailCollapsed ? '1' : '0'); } catch { /* noop */ }
  }, [rightRailCollapsed]);
  // Iter 189b — History panel toggle (top-bar History button).
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  useEffect(() => {
    const onDocClick = (e) => {
      // Iter 155 — Drag-end synthetic click guard: when a Fluid Text drag
      // just ended, suppress section-selection churn that would otherwise
      // shift focus / steal the user's attention.
      if (window.__wbDragEndedAt && (Date.now() - window.__wbDragEndedAt) < 300) return;
      // Ignore clicks inside any builder overlay/control surface — they
      // run their own actions and should not also flip section selection.
      const inOverlay = e.target.closest && (
        e.target.closest('[data-testid="edit-panel"]') ||
        e.target.closest('[data-testid="add-section-modal"]') ||
        e.target.closest('[role="dialog"]') ||
        e.target.closest('[data-wb-overlay]') ||
        // Hover toolbar + its popovers (More menu, Height submenu, etc).
        e.target.closest('[data-testid^="section-controls-"]') ||
        e.target.closest('[data-testid^="section-more-menu-"]') ||
        e.target.closest('[data-testid^="section-height-menu-"]') ||
        // The "+ ADD BLOCK" pill and section label pill.
        e.target.closest('[data-testid^="section-add-block-"]')
      );
      if (inOverlay) return;
      // Walk up the DOM to find the section the click landed in.
      let node = e.target;
      while (node && node !== document.body) {
        if (node.dataset && node.dataset.blockId) {
          setSelectedBlockId(node.dataset.blockId);
          return;
        }
        node = node.parentNode;
      }
      // Click outside every section → clear selection.
      setSelectedBlockId(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setSelectedBlockId(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // ── Iter 151 — Image editing system (Squarespace-class).
  // When a user double-clicks any [data-wb-image] element, WBImage dispatches
  // a `wb:edit-image` CustomEvent → we surface the EditDrawer (Iter 178h:
  // previously this opened a floating white toolbar). The drawer reads/writes
  // content._image_frames[field] via FloatingImageToolbar rendered inline.
  const [editingImage, setEditingImage] = useState(null); // { blockId, field, rect }
  useEffect(() => {
    const onEditImage = (e) => {
      const { blockId, field, rect } = e.detail || {};
      if (!blockId || !field) return;
      setEditingImage({ blockId, field, rect });
      // Iter 178h — also open the side drawer so the image tools live there.
      setEditPanelBlock(blockId);
    };
    window.addEventListener('wb:edit-image', onEditImage);
    return () => window.removeEventListener('wb:edit-image', onEditImage);
  }, []);

  // ── Iter 154 — Floating Text Toolbar (Squarespace/Canva/Figma-class).
  // InlineText dispatches `wb:focus-text` on focus → toolbar anchors above
  // the focused field. The toolbar writes per-field typography into
  // block.content._field_styles[field].
  const [editingText, setEditingText] = useState(null); // { blockId, field, rect }
  useEffect(() => {
    const onFocusText = (e) => {
      const { blockId, field, rect } = e.detail || {};
      if (!blockId || !field) return;
      setEditingText({ blockId, field, rect });
    };
    window.addEventListener('wb:focus-text', onFocusText);
    return () => window.removeEventListener('wb:focus-text', onFocusText);
  }, []);

  // ── Iter 157 — Toolbar priority system.
  // Only ONE editing surface should be visible at a time. Priority:
  //   text > image > button > fluid-drag-hover > section.
  // Set body[data-wb-toolbar-active] so CSS can fade out lower-priority
  // toolbars; clear selectedBlockId so the SectionControlCard doesn't
  // visually compete with the floating editors.
  // Iter 166 — also clear the section card whenever ANY inner editing
  // surface is active (inline button popover, fluid-drag hover handle)
  // so the white card doesn't cover the text the user is editing. Card
  // returns automatically when those surfaces close + the user clicks
  // the section again.
  // Iter 178l — Only update body attr / clear selection when the ACTIVE TYPE
  // changes. Previously `fluidDrag?.hoverTarget` was a dependency, so this
  // useEffect fired on every mousemove between text spans (the hoverTarget
  // object is a new reference). Each fire called `setSelectedBlockId(null)`
  // which triggered a parent re-render, which propagated to the section
  // toolbars and re-engaged their CSS transitions → strobe.
  const activeType = editingText ? 'text'
    : editingImage ? 'image'
    : inlineBtn?.editorState ? 'button'
    : fluidDrag?.hoverTarget ? 'fluid'
    : null;
  useEffect(() => {
    if (activeType) {
      document.body.setAttribute('data-wb-toolbar-active', activeType);
      setSelectedBlockId(null);
    } else {
      document.body.removeAttribute('data-wb-toolbar-active');
    }
    return () => { document.body.removeAttribute('data-wb-toolbar-active'); };
  }, [activeType]);

  // Open the page the vendor clicked "Edit" on (Pages → ?page=<id>).
  const openedPageRef = useRef(false);
  useEffect(() => {
    if (openedPageRef.current || loading) return;
    openedPageRef.current = true;
    const pid = new URLSearchParams(location.search).get('page');
    if (pid && pid !== 'home') {
      const pg = pages.find(p => p.id === pid);
      if (pg) switchToPage(pg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pages]);

  // Land on the exact scroll position the vendor was viewing in Live Preview.
  useEffect(() => {
    if (loading) return;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('wb:editScroll') || 'null'); } catch { /* noop */ }
    if (!saved) return;
    const pid = new URLSearchParams(location.search).get('page') || 'home';
    if ((saved.page || 'home') !== pid || pid !== (activePage?.id || 'home')) return;
    sessionStorage.removeItem('wb:editScroll');
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="editor-canvas"]');
      if (el) el.scrollTop = saved.y || 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, activePage]);

  const renderBlockEditor = (block) => {
    switch (block.type) {
      case 'hero': return <HeroEditor block={block} updateBlock={updateBlock} />;
      case 'banner': return <BannerEditor block={block} updateBlock={updateBlock} />;
      case 'about': return <AboutEditor block={block} updateBlock={updateBlock} />;
      case 'custom_image': return <CustomImageEditor block={block} updateBlock={updateBlock} />;
      case 'gallery': return <GalleryBlockEditor block={block} updateBlock={updateBlock} />;
      case 'products': return <FeaturedProductsEditor block={block} updateBlock={updateBlock} allProducts={allProducts} />;
      case 'donations': return <DonationsEditor block={block} updateBlock={updateBlock} />;
      case 'signup': return <SignupEditor block={block} updateBlock={updateBlock} />;
      case 'custom_text': return <CustomTextEditor block={block} updateBlock={updateBlock} />;
      case 'video': return <VideoEditor block={block} updateBlock={updateBlock} />;
      case 'testimonial': return <TestimonialEditor block={block} updateBlock={updateBlock} />;
      case 'spacer': return <SpacerEditor block={block} updateBlock={updateBlock} />;
      case 'booking': return <BookingEditor block={block} updateBlock={updateBlock} />;
      case 'form': return <FormBlockEditor block={block} updateBlock={updateBlock} />;
      case 'embed': return <EmbedBlockEditor block={block} updateBlock={updateBlock} />;
      case 'single_image': return <SingleImageBlockEditor block={block} updateBlock={updateBlock} />;
      case 'marquee': return <MarqueeEditor block={block} updateBlock={updateBlock} />;
      // Premium / iter141+ block types use the universal introspecting editor.
      // It handles services, testimonials, features, team, editorial, stats,
      // cta, logos, data_table, product_grid, product_detail, cart_table,
      // footer, faq, contact, split — anything content-shaped.
      case 'services':
      case 'testimonials':
      case 'features':
      case 'team':
      case 'editorial':
      case 'stats':
      case 'cta':
      case 'logos':
      case 'data_table':
      case 'product_grid':
      case 'product_detail':
      case 'cart_table':
      case 'footer':
      case 'faq':
      case 'contact':
      case 'split':
        return <UniversalContentEditor block={block} onChange={(next) => updateBlockContent(block.id, next)} hideSectionMeta />;
      default: return <UniversalContentEditor block={block} onChange={(next) => updateBlockContent(block.id, next)} hideSectionMeta />;
    }
  };

  if (loading) return (
    <div className="fixed inset-0 z-[120] bg-[#0E0E23] flex flex-col" data-testid="content-editor-loading">
      <div className="h-14 shrink-0 border-b border-white/[0.06]" />
      <div className="flex-1 flex items-center justify-center"><Loader2 size={28} className="animate-spin text-[#8B5CF6]" /></div>
    </div>
  );

  const sortedBlocks = [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
  const editBlock = sortedBlocks.find(b => b.id === editPanelBlock);
  const colorBlock = sortedBlocks.find(b => b.id === colorPanelBlock);
  const layoutsBlock = sortedBlocks.find(b => b.id === layoutsPanelBlock);
  // Same design tokens + surface as Pages → Live Preview (WYSIWYG).
  const siteTokens = resolveSiteTokens(siteTheme, siteMeta?.settings);
  const isMobileCanvas = device === 'mobile' || viewMode === 'mobile';

  const exitEditor = async () => {
    if (hasChanges && !(await themedConfirm('You have unsaved changes. Exit anyway?'))) return;
    navigate('/website-builder/pages');
  };

  const iconBtn = 'w-8 h-8 grid place-items-center rounded-lg text-[#D1D5DB] hover:text-white hover:bg-white/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent';

  return (
    <BuilderDeviceContext.Provider value={{ device, setDevice }}>
    <div className="fixed inset-0 z-[120] bg-[#0E0E23] flex flex-col" data-testid="content-editor">
      {/* Squarespace-style slim edit bar (56px). The website below renders
          at full width exactly as in Live Preview — this bar never overlaps it. */}
      <style>{`
        @keyframes wb-editor-chrome-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        .wb-editor-chrome { animation: wb-editor-chrome-in 320ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>
      <div className="h-14 shrink-0 border-b border-white/[0.06] bg-[#0E0E23] flex items-center gap-3 px-4 wb-editor-chrome" data-testid="editor-topbar">
        <button onClick={() => setLeftRailCollapsed(v => !v)} className={`${iconBtn} ${!leftRailCollapsed ? 'bg-white/10 text-white' : ''}`} title="Pages & layers" data-testid="left-rail-toggle-top"><PanelLeft size={15} /></button>
        <button onClick={exitEditor} className="h-8 px-3 rounded-lg text-[12px] font-semibold text-[#D1D5DB] hover:text-white hover:bg-white/10 transition-colors inline-flex items-center gap-1.5" data-testid="editor-exit-btn">
          <XIcon size={14} /> Exit
        </button>
        <div className="w-px h-6 bg-white/10" />
        <div className="min-w-0 flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Editing</span>
          <span className="text-[14px] font-bold text-white truncate" style={{ fontFamily: 'Syne, Inter, sans-serif' }} data-testid="editor-page-title">{activePage?.title || 'Home'}</span>
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#64748B] whitespace-nowrap" data-testid="editor-save-state">
            {saving ? 'Saving…' : hasChanges ? 'Unsaved' : 'Saved'}
          </span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-0.5 bg-white/[0.04] rounded-md p-0.5">
          <button onClick={() => { setViewMode('desktop'); setDevice('desktop'); }} className={`p-1.5 rounded ${!isMobileCanvas ? 'bg-white/10 text-white' : 'text-[#9CA3AF] hover:text-white'} transition-colors`} title="Desktop" data-testid="vm-desktop"><Monitor size={13} /></button>
          <button onClick={() => { setViewMode('mobile'); setDevice('mobile'); }} className={`p-1.5 rounded ${isMobileCanvas ? 'bg-white/10 text-white' : 'text-[#9CA3AF] hover:text-white'} transition-colors`} title="Mobile" data-testid="vm-mobile"><Smartphone size={13} /></button>
        </div>
        <button onClick={undo} disabled={!canUndo} className={iconBtn} title={canUndo ? 'Undo (⌘Z)' : 'Nothing to undo'} data-testid="editor-undo"><Undo2 size={15} /></button>
        <button onClick={redo} disabled={!canRedo} className={iconBtn} title={canRedo ? 'Redo (⌘⇧Z)' : 'Nothing to redo'} data-testid="editor-redo"><Redo2 size={15} /></button>
        <button onClick={() => setColorPanelBlock(sortedBlocks[0]?.id)} className={iconBtn} title="Site Styles" data-testid="editor-styles"><Palette size={14} /></button>
        <button
          onClick={() => { setAiMode('section'); setAiInsertAfter(sortedBlocks.length - 1); setAiModalOpen(true); }}
          className="px-3 h-8 rounded-lg text-[10px] font-bold uppercase tracking-[0.14em] text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_0_16px_rgba(139,92,246,0.4)] transition-shadow flex items-center gap-1.5"
          data-testid="editor-ai-btn"
        >
          <Sparkles size={11} /> AI
        </button>
        <button
          onClick={() => {
            const sub = siteMeta?.subdomain;
            if (!sub) { toast.error('Save your site first to preview.'); return; }
            window.open(`/site/${sub}?preview=customer`, '_blank', 'noopener,noreferrer');
          }}
          className={iconBtn}
          title="Preview as Customer (opens public site in new tab)"
          data-testid="editor-preview-as-customer"
        >
          <Play size={14} />
        </button>
        <div className="w-px h-6 bg-white/10" />
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="px-4 h-8 rounded-lg bg-white text-[#0E0E23] text-[12px] font-bold hover:bg-[#F5F5F5] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="editor-save-btn"
        >
          {saving ? 'Saving…' : hasChanges ? 'Save' : 'Saved'}
        </button>
        <PublishBar />
        <button onClick={() => setRightRailCollapsed(v => !v)} className={`${iconBtn} ${!rightRailCollapsed ? 'bg-white/10 text-white' : ''}`} title="Inspector" data-testid="right-rail-toggle-top"><PanelRight size={15} /></button>
      </div>

      {/* ── Website canvas. Rails float ABOVE it (absolute) so opening them
          never squeezes the site or changes its responsive layout. ── */}
      <div className="flex-1 relative overflow-hidden" data-testid="editor-workspace">
      <div className="absolute inset-0 overflow-y-auto bg-[#0E0E23]" style={{ scrollbarGutter: 'stable' }} data-testid="editor-canvas">
        {/* Mobile-mode CSS overrides — same as the template preview phone frame */}
        <style>{`
          .wb-builder-mobile .grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .wb-builder-mobile [class*="md:grid-cols-"],
          .wb-builder-mobile [class*="sm:grid-cols-"],
          .wb-builder-mobile [class*="lg:grid-cols-"],
          .wb-builder-mobile [class*="grid-cols-"] { grid-template-columns: 1fr !important; }
          .wb-builder-mobile img { max-width: 100% !important; height: auto !important; max-height: 320px !important; object-fit: cover !important; }
          .wb-builder-mobile section { padding-left: 18px !important; padding-right: 18px !important; padding-top: 40px !important; padding-bottom: 40px !important; }
          .wb-builder-mobile h1 { font-size: 2rem !important; line-height: 1.1 !important; }
          .wb-builder-mobile h2 { font-size: 1.55rem !important; line-height: 1.18 !important; }
          .wb-builder-mobile .text-7xl, .wb-builder-mobile .text-8xl, .wb-builder-mobile .text-6xl, .wb-builder-mobile .text-5xl { font-size: 1.75rem !important; line-height: 1.1 !important; }
          .wb-builder-mobile .text-4xl, .wb-builder-mobile .text-3xl { font-size: 1.3rem !important; }
          .wb-builder-mobile .text-2xl, .wb-builder-mobile .text-xl, .wb-builder-mobile .text-lg { font-size: 1.05rem !important; }
          .wb-builder-mobile [class*="max-w-"] { max-width: 100% !important; }
          .wb-builder-mobile [class*="min-h-"] { min-height: 380px !important; }
          .wb-builder-mobile .columns-3, .wb-builder-mobile .columns-2 { columns: 1 !important; }
          .wb-builder-mobile .px-16, .wb-builder-mobile .px-8, .wb-builder-mobile .md\\:px-16 { padding-left: 18px !important; padding-right: 18px !important; }
          .wb-builder-mobile .py-32, .wb-builder-mobile .py-24, .wb-builder-mobile .py-20, .wb-builder-mobile .py-16 { padding-top: 44px !important; padding-bottom: 44px !important; }
          .wb-builder-mobile .gap-12, .wb-builder-mobile .gap-10, .wb-builder-mobile .gap-8 { gap: 20px !important; }
          .wb-builder-mobile .h-\\[600px\\], .wb-builder-mobile .h-\\[480px\\] { height: 260px !important; }

          /* Inline-edit hover affordance — violet dashed outline + pencil
             badge on any text/heading/paragraph that's mapped to a content
             field. Driven by data-attributes (NOT className/style) because
             React reconciliation wipes imperative class/style mutations.
             Iter 160 — DO NOT force cursor: text here. The hover affordance
             should respect the underlying drag intent (.wb-inline-text and
             [data-wb-fluid-hover='1'] both set cursor: grab so users see
             they can move the element). The text/I-beam cursor only shows
             up once the user is actively editing (data-wb-active-edit). */
          [data-wb-edit-hover='1'] { position: relative !important; }
          [data-wb-edit-hover='1']::after {
            content: ''; position: absolute; inset: -4px; border: 1.5px dashed rgba(139,92,246,0.7);
            border-radius: 4px; pointer-events: none; z-index: 1;
            box-shadow: 0 0 0 1px rgba(255,255,255,0.5);
          }
          [data-wb-edit-hover='1']::before {
            content: '\\270E  Click to edit';
            position: absolute; top: -22px; left: 0;
            font: 600 9px/1 system-ui, sans-serif; letter-spacing: 0.04em; text-transform: uppercase;
            background: #8B5CF6; color: #fff; padding: 4px 7px; border-radius: 4px;
            white-space: nowrap; pointer-events: none; z-index: 2;
            box-shadow: 0 4px 12px rgba(139,92,246,0.45);
          }
          /* Active edit state - driven by data-wb-active-edit so we do not
             accidentally paint elements that are always-contentEditable via
             the InlineText component. */
          [data-wb-active-edit='1'] {
            outline: 2px solid rgba(139,92,246,0.85) !important;
            outline-offset: 2px !important;
            background: rgba(139,92,246,0.06) !important;
            cursor: text !important;
            border-radius: 2px;
          }
          [data-wb-active-edit='1']::before,
          [data-wb-active-edit='1']::after { display: none !important; }

          /* Iter 161 — Hide every floating section overlay while the user
             is actively dragging an element. useFluidTextDrag sets
             body[data-wb-dragging='1'] on drag start and clears it on
             drag end. The white SectionControlCard menu, the section
             hover overlay (border + Add-Block pill), and the BlockToolbar
             pill all fade to opacity:0 + pointer-events:none so the user
             can move buttons / text / images freely inside the section
             without the menu obstructing the drop target. */
          body[data-wb-dragging='1'] [data-wb-scc],
          body[data-wb-dragging='1'] [data-testid^='block-toolbar-'],
          body[data-wb-dragging='1'] [data-testid^='section-add-block-'],
          body[data-wb-dragging='1'] [data-testid^='section-hover-'] {
            opacity: 0 !important;
            pointer-events: none !important;
            transition: opacity 80ms ease-out;
          }

          /* Iter 160 — Buttons in the canvas are positioned via the Fluid
             grid (mouse drag, CSS translate). They no longer use HTML5
             draggable so we drop the [draggable=true] qualifier. The
             grab cursor still hints "I can be moved"; the active grabbing
             state is owned by useFluidTextDrag while the drag is in flight.

             Iter 182 — REMOVED the :hover box-shadow ring and ::before
             grab-handle pseudo-element on canvas buttons. Both rules
             were fired by the NATIVE :hover pseudo, which toggles on/off
             as the cursor crosses the button boundary or moves over
             adjacent text spans (mouse pointer movement is naturally
             jittery). Result: 5-10 Hz visual flicker (purple ring
             pulsing plus a small pill appearing/disappearing) reported
             by the user in their 8th flicker video. The grab cursor
             still tells the user "I can be moved"; no flicker source
             on the button itself. */
          [data-wb-button="1"] { position: relative; cursor: grab; }
          [data-wb-button="1"]:active { cursor: grabbing; }
          .wb-sortable-active { opacity: 0.35 !important; }
          /* Squarespace parity — section chrome only appears on the section
             under the cursor. Pure visibility toggle keyed off the SECTION's
             :hover (never the chip's own), so there is no hover loop/strobe. */
          .builder-edit-canvas [data-testid^="live-block-"] .wb-hover-chip { visibility: hidden; }
          .builder-edit-canvas [data-testid^="live-block-"]:hover .wb-hover-chip,
          .builder-edit-canvas [data-testid^="live-block-"]:focus-within .wb-hover-chip { visibility: visible; }
          .wb-sortable-target { outline: 2px dashed #8B5CF6 !important; outline-offset: 4px !important; border-color: #8B5CF6 !important; }
        `}</style>
        <EditableHoverBoundary>
        <SiteSurface
          tokens={siteTokens}
          className={`builder-edit-canvas min-h-full ${isMobileCanvas ? 'mx-auto max-w-[390px] wb-builder-mobile' : 'w-full'}`}
        >
        <div
          ref={canvasRef}
          onDoubleClick={(e) => {
            const target = e.target;
            // Walk up to find the section block
            let node = target;
            while (node && !(node.dataset && node.dataset.testid && node.dataset.testid.startsWith('live-block-'))) {
              node = node.parentNode;
              if (!node || node === document.body) break;
            }
            if (!node || !node.dataset?.testid) return;

            const id = node.dataset.testid.replace('live-block-', '');
            const blk = blocks.find(b => b.id === id);
            if (!blk) return;

            // Open Image Editor if the click was on an img, an upload zone, or any image-bearing block
            const isImg = target.tagName === 'IMG';
            const placeholder = target.closest && (target.closest('[data-testid="image-upload-zone"]') || target.closest('.wb-image-zone'));
            const blockHasImage = !!blk.content?.image_url || ['hero', 'banner', 'about', 'custom_image', 'single_image', 'gallery'].includes(blk.type);
            const imgInBlock = node.querySelector && node.querySelector('img');

            if (isImg || placeholder || (blockHasImage && imgInBlock)) {
              const url = blk.content?.image_url || (isImg ? target.src : imgInBlock?.src || '');
              setImageEditorBlock({ blockId: id, field: 'image_url', imageUrl: url });
            }
          }}
        >
          {/* Site Header (chrome) — editable inline */}
          <CanvasHeader theme={siteTheme || {}} site={siteMeta} onSaved={(patch) => setSiteTheme(t => ({ ...(t || {}), ...patch }))} />

          {/* Add section at top */}
          {sortedBlocks.length > 0 && <AddSectionPill onClick={() => { setAddSectionAfter(-1); setAddSectionOpen(true); }} testid="add-section-top" />}

          {sortedBlocks.map((block, idx) => (
            <React.Fragment key={block.id}>
              {renderSiteBlock(block, {
                allProducts,
                tokens: siteTokens,
                onEdit: updateBlock,
                onMediaPick: (blockId, field) => openMediaPicker(blockId, field),
                onDelete: () => deleteBlock(block.id),
                onMoveUp: () => moveBlock(block.id, -1),
                onMoveDown: () => moveBlock(block.id, 1),
                isFirst: idx === 0,
                isLast: idx === sortedBlocks.length - 1,
                onDuplicate: duplicateBlock,
                onAddBlock: (sectionBlockId) => setAddBlockTarget(sectionBlockId),
                onViewLayouts: (sectionBlockId) => setLayoutsPanelBlock(sectionBlockId),
                onEditSection: (sectionBlockId) => { setEditPanelBlock(sectionBlockId); setSelectedBlockId(null); },
                onFavorite: favoriteBlock,
                onSetHeight: setSectionHeight,
                onSetAttached: setSectionAttached,
                isSelected: selectedBlockId === block.id,
              })}
              <AddSectionPill onClick={() => { setAddSectionAfter(idx); setAddSectionOpen(true); }} testid={`add-section-after-${idx}`} />
            </React.Fragment>
          ))}

          {sortedBlocks.length === 0 && (
            <div className="py-32 text-center">
              <div className="w-20 h-20 rounded-3xl bg-gray-100 flex items-center justify-center mx-auto mb-6"><Layout size={36} className="text-gray-300" /></div>
              <h2 className="text-2xl font-bold text-gray-800 font-syne">Start building</h2>
              <p className="text-gray-400 mt-2 max-w-md mx-auto">Add your first section — choose from heros, galleries, services, and more.</p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button onClick={() => { setAddSectionAfter(-1); setAddSectionOpen(true); }} className="px-6 py-2.5 rounded-full bg-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-[0.18em] hover:bg-[#2563EB] transition-all" data-testid="empty-add-section">
                  + Add Section
                </button>
                <button
                  onClick={() => { setAiMode('site'); setAiInsertAfter(-1); setAiModalOpen(true); }}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-[0.18em] hover:shadow-[0_0_24px_rgba(139,92,246,0.4)] transition-all flex items-center gap-2"
                  data-testid="empty-ai-build"
                >
                  <Sparkles size={11} /> Build with AI
                </button>
              </div>
            </div>
          )}

          {/* Site Footer (chrome) — editable inline */}
          <CanvasFooter theme={siteTheme || {}} site={siteMeta} onSaved={(patch) => setSiteTheme(t => ({ ...(t || {}), ...patch }))} />
        </div>
        </SiteSurface>
        </EditableHoverBoundary>
      </div>
        {!leftRailCollapsed && (
        <div className="absolute top-0 bottom-0 left-0 z-[40] flex shadow-[12px_0_40px_rgba(0,0,0,0.35)]" data-testid="editor-left-rail-layer">
        <LeftRailPagesTree
          pages={pages}
          activePage={activePage}
          blocks={sortedBlocks}
          switchToPage={switchToPage}
          onNewPage={(title) => { setNewPageTitle(title); setTimeout(handleCreatePage, 30); }}
          onDeletePage={handleDeletePage}
          onRenamePage={handleRenamePageSave}
          selectedBlockId={selectedBlockId}
          onSelectBlock={setSelectedBlockId}
          collapsed={leftRailCollapsed}
          onToggleCollapse={() => setLeftRailCollapsed(v => !v)}
        />
        </div>
        )}
        {!rightRailCollapsed && (
        <div className="absolute top-0 bottom-0 right-0 z-[40] flex shadow-[-12px_0_40px_rgba(0,0,0,0.35)]" data-testid="editor-right-rail-layer">
        <RightRailInspector
          selectedBlock={sortedBlocks.find(b => b.id === selectedBlockId) || null}
          onSetSectionHeight={setSectionHeight}
          onSetContainerWidth={(id, width) => {
            const blk = sortedBlocks.find(b => b.id === id);
            if (!blk) return;
            const pd = { ...(blk.content?._premium_defaults || {}), container_width: width };
            updateBlockContent(id, { ...(blk.content || {}), _premium_defaults: pd, container_width: width });
          }}
          onSetAlign={(id, align) => {
            const blk = sortedBlocks.find(b => b.id === id);
            if (!blk) return;
            const fs = { ...(blk.content?._field_styles || {}) };
            fs.title = { ...(fs.title || {}), align };
            updateBlockContent(id, { ...(blk.content || {}), _field_styles: fs, align });
          }}
          onSetBackground={(id, color) => {
            const blk = sortedBlocks.find(b => b.id === id);
            if (!blk) return;
            const s = { ...(blk.content?._style || {}), background_color: color };
            updateBlockContent(id, { ...(blk.content || {}), _style: s, background_color: color });
          }}
          onOpenFullEditor={(id) => { setEditPanelBlock(id); setSelectedBlockId(null); }}
          onOpenAi={(id) => { setAiMode('rewrite'); setAiInsertAfter(sortedBlocks.findIndex(b => b.id === id)); setAiModalOpen(true); }}
          collapsed={rightRailCollapsed}
          onToggleCollapse={() => setRightRailCollapsed(v => !v)}
        />
        </div>
        )}
      </div>

      {/* Inline formatting toolbar (shows on text selection) */}
      <InlineSelectionToolbar />
      {/* Iter 198g — Figma-style column ruler pinned to top of active section during drag */}
      <ColumnRuler />
      {/* ── Fluid drag handle (Iter 158 — REMOVED) ──
          The floating handle was overlapping draggable buttons' top-left
          corner and breaking their parent block's :hover chain, causing
          the button (which used group-hover/block:opacity-100) to
          flash on and off. Direct drag from iter 155 (mousedown on the
          element + 5px threshold) covers the same affordance without
          the visual disruption.
          <FluidDragHandle hoverTarget={fluidDrag.hoverTarget} startDrag={fluidDrag.startDrag} resetOffset={fluidDrag.resetOffset} /> */}

      {/* Iter 190j — REMOVED: standalone `fluid-device-toggle` at top-right
          corner. It duplicated the device switcher already surfaced in the
          editor top-bar row 2 (`vm-desktop` / `vm-mobile`). Per user
          request — one device toggle in the app is enough. The vm-* buttons
          in the top bar now call BOTH `setViewMode` (canvas clamp) AND
          `setDevice` (fluid-offsets key) so functionality is preserved. */}

      {/* ── Multi-select status pill ─────────────────────────────── */}
      {multiSelect.selected.size > 1 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[160] wb-multi-select-toolbar"
          data-testid="multi-select-bar"
        >
          <span>{multiSelect.selected.size} selected</span>
          <span className="sep" />
          <button onClick={multiSelect.clear} data-testid="multi-select-clear">Clear</button>
        </div>
      )}

      {/* ── Block Edit Drawer (Iter 150 redesign — accordion-based) ──
          Iter 178h — `imageEditing` is forwarded to surface a new "Image"
          accordion at the top whenever the user double-clicks an image,
          collapsing the previous floating white toolbar into the drawer. */}
      {editBlock && (() => {
        // Build the inline image-editor props if a double-clicked image
        // belongs to THIS edit-drawer block.
        let imageEditingProps = null;
        if (editingImage && editingImage.blockId === editBlock.id) {
          const content = editBlock.content || {};
          const frames = content._image_frames || {};
          const f = editingImage.field;
          const frame = frames[f] || {};
          // Resolve/write nested paths of arbitrary depth:
          //   "image_url"                → content.image_url
          //   "gallery.0"                → content.gallery[0] (string or {url})
          //   "members.2.photo"          → content.members[2].photo   (Iter 190h — team)
          const parseField = () => f.split('.');
          const resolveImage = () => {
            const parts = parseField();
            let cur = content[parts[0]];
            for (let i = 1; i < parts.length; i++) {
              if (cur == null) return undefined;
              const key = /^\d+$/.test(parts[i]) ? Number(parts[i]) : parts[i];
              cur = cur[key];
            }
            if (typeof cur === 'string') return cur;
            return cur?.url;
          };
          const setImage = (newUrl) => {
            const parts = parseField();
            if (parts.length === 1) {
              updateBlock(editBlock.id, parts[0], newUrl);
              return;
            }
            // Deep-clone the top-level array/object then walk to the leaf.
            const rootKey = parts[0];
            const clone = (v) => (Array.isArray(v) ? [...v] : { ...(v || {}) });
            const rootValue = content[rootKey];
            const root = clone(rootValue);
            let node = root;
            for (let i = 1; i < parts.length - 1; i++) {
              const key = /^\d+$/.test(parts[i]) ? Number(parts[i]) : parts[i];
              node[key] = Array.isArray(node[key]) ? [...node[key]] : { ...(node[key] || {}) };
              node = node[key];
            }
            const leaf = parts[parts.length - 1];
            const leafKey = /^\d+$/.test(leaf) ? Number(leaf) : leaf;
            if (parts.length === 2) {
              // legacy: gallery.idx  → item may be raw string or {url:'...'}
              const wasObj = node[leafKey] && typeof node[leafKey] !== 'string';
              node[leafKey] = wasObj ? { ...node[leafKey], url: newUrl } : newUrl;
            } else {
              node[leafKey] = newUrl;
            }
            updateBlock(editBlock.id, rootKey, root);
          };
          imageEditingProps = {
            blockId: editBlock.id,
            field: f,
            imageUrl: resolveImage(),
            frame,
            onUpdateFrame: (nextFrame) => updateBlock(editBlock.id, '_image_frames', { ...frames, [f]: nextFrame }),
            onReplace: (url) => setImage(url),
            onMediaPick: () => openMediaPicker(editBlock.id, f),
            onRemove: () => { setImage(''); setEditingImage(null); },
            onClose: () => setEditingImage(null),
          };
        }
        return (
          <EditDrawer
            block={editBlock}
            imageEditing={imageEditingProps}
            onClose={() => { setEditPanelBlock(null); setEditingImage(null); }}
            onUpdate={(id, field, value) => {
              if (field === '__visibility__') { toggleVisibility(id); return; }
              updateBlock(id, field, value);
            }}
            onMediaPick={(path) => {
              const parts = String(path || '').split('-');
              const fieldKey = parts.length > 1 ? parts.slice(1).join('-') : 'image_url';
              openMediaPicker(editBlock.id, fieldKey);
            }}
            renderContentEditor={(b) => (
              <>
                {renderBlockEditor(b)}
              </>
            )}
          />
        );
      })()}

      {/* ── Iter 178h — Floating Image Toolbar removed.
            All image-editing tools (Replace, Library, Crop, Frame, Settings,
            Remove) now live inside the EditDrawer "Image" accordion via the
            FloatingImageToolbar's new `inline` mode. */}

      {/* SECTION COLOR PANEL */}
      {colorBlock && (
        <SectionColorPanel
          block={colorBlock}
          palette={palette}
          sectionContextLabel={`Editing ${blockLabels[colorBlock.type] || colorBlock.type}`}
          onClose={() => setColorPanelBlock(null)}
          onUpdate={updateBlock}
        />
      )}

      {/* LAYOUTS PANEL */}
      {layoutsBlock && (
        <LayoutsPanel
          block={layoutsBlock}
          open={!!layoutsBlock}
          onClose={() => setLayoutsPanelBlock(null)}
          onApply={(variantOrId) => { applyLayout(layoutsBlock.id, variantOrId); setLayoutsPanelBlock(null); }}
          onChangeLayout={(blockId, newLayout) => setBlockLayout(blockId, newLayout)}
        />
      )}

      {/* ADD A SECTION MODAL */}
      <AddSectionModal
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        onAdd={(tpl) => insertBlocksFromTemplate(tpl, addSectionAfter)}
      />

      {/* AI GENERATE MODAL */}
      <AIGenerateModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        mode={aiMode}
        onInsert={(aiBlocks) => insertAiBlocks(aiBlocks, aiInsertAfter)}
      />

      {/* ITER 189b — HISTORY PANEL */}
      <HistoryPanel
        open={historyPanelOpen}
        onClose={() => setHistoryPanelOpen(false)}
        historyList={historyList}
        historyIndexNow={historyIndexNow}
        onJumpTo={(idx) => jumpTo(idx)}
      />

      {/* ADD A BLOCK FLOATING MENU (anchored to viewport center for simplicity) */}
      {/* Iter 175k — Bug fix: was wrapped in a redundant `fixed inset-0 z-[180]`
          div which collided with AddBlockMenu's own backdrop (also z-180 with
          `backdrop-blur-sm`). The stacked backdrop-filter layer recomputed on
          every hover-driven re-render, causing the modal to visibly flicker
          on/off when the mouse moved over the Text/Image/Button icon tiles.
          AddBlockMenu already self-centers and self-backdrops, so render it
          directly. */}
      {addBlockTarget && (
        <AddBlockMenu
          open={!!addBlockTarget}
          onClose={() => setAddBlockTarget(null)}
          onPick={(type) => {
                // INTENT: user clicked the "+" on a specific section. We
                // ALWAYS append button/text/image primitives INTO that
                // section. Three-tier strategy:
                //   1) If the section natively renders `content.buttons`
                //      (agency_*, hero variants, premium ext blocks),
                //      append there → button shows up exactly where the
                //      designer placed it in the layout.
                //   2) Else if the section uses `content._style.buttons`
                //      (legacy About/Hero/CTA via StyleButtons), append
                //      there → button shows up immediately under the
                //      text in the same column.
                //   3) Else fall back to the universal `_extras_*` zone
                //      rendered by FullBlock at the bottom.
                // Only full-section template types (Gallery, Products,
                // FAQ, Footer, etc.) create a new sibling block.
                const target = blocks.find(b => b.id === addBlockTarget);
                const targetIdx = sortedBlocks.findIndex(b => b.id === addBlockTarget);
                const INSERT_AS_CHILD = new Set([
                  'button', 'banner', 'custom_text', 'custom_image', 'single_image',
                ]);
                // Block types that natively read content.buttons in their renderer.
                const NATIVE_CONTENT_BUTTONS = new Set([
                  'hero', 'banner', 'about', 'cta', 'split', 'editorial',
                  'agency_glass_stat', 'agency_cta_band',
                ]);
                const tryAppendInside = () => {
                  if (!target) return false;
                  if (!INSERT_AS_CHILD.has(type)) return false;
                  const c = target.content || {};
                  if (type === 'button' || type === 'banner') {
                    const newBtn = { text: 'New Button', link: '#', color: '' };
                    // Tier 1 — native content.buttons (most premium / agency / hero blocks)
                    if (NATIVE_CONTENT_BUTTONS.has(target.type) || Array.isArray(c.buttons)) {
                      const nextButtons = [...(Array.isArray(c.buttons) ? c.buttons : []), newBtn];
                      updateBlock(target.id, 'buttons', nextButtons);
                      toast.success('Button added — right under the text');
                      return true;
                    }
                    // Tier 2 — legacy _style.buttons (About/Hero via StyleButtons)
                    const styleObj = c._style || {};
                    if (Array.isArray(styleObj.buttons) || ['about', 'hero', 'cta'].includes(target.type)) {
                      const nextButtons = [...((styleObj.buttons) || []), newBtn];
                      updateBlock(target.id, '_style', { ...styleObj, buttons: nextButtons });
                      toast.success('Button added — right under the text');
                      return true;
                    }
                    // Tier 3 — extras zone (fallback)
                    const nextButtons = [
                      ...((c._extras_buttons || [])),
                      newBtn,
                    ];
                    updateBlock(target.id, '_extras_buttons', nextButtons);
                    toast.success('Button added to this section');
                    return true;
                  }
                  if (type === 'custom_text') {
                    const arr = Array.isArray(c._extras_text_blocks) ? c._extras_text_blocks : [];
                    // Iter 190 — start with empty text so the placeholder
                    // ("New text — click to edit") is shown via InlineText's
                    // `data-placeholder` attribute. When the user clicks, they
                    // land on an empty editor and can just type instead of
                    // having to select-all-and-replace the "New text …"
                    // seed string.
                    const newEntry = { id: `tx-${Date.now()}`, text: '', kind: 'p' };
                    updateBlock(target.id, '_extras_text_blocks', [...arr, newEntry]);
                    toast.success('Text added — click to edit, drag to position');
                    return true;
                  }
                  if (type === 'single_image' || type === 'custom_image') {
                    const arr = Array.isArray(c._extras_images) ? c._extras_images : [];
                    const next = [...arr, { url: '', alt: '' }];
                    updateBlock(target.id, '_extras_images', next);
                    toast.success('Image added to this section');
                    return true;
                  }
                  return false;
                };
                const appendedInside = tryAppendInside();
                if (!appendedInside) {
                  addBlockAt(type, targetIdx);
                }
                // Iter 190 — Add-Block flicker fix.
                // Removed the previous `setTimeout(sect.scrollIntoView({behavior:'smooth'}) + extras.animate(outline...))`
                // combo. Both were causing the "new button briefly disappears / flashes on add"
                // bug (user video 2026-07-14):
                //   1. `scrollIntoView({behavior: 'smooth'})` fired a ~500ms smooth-scroll that
                //      re-triggered FluidOverlayLayer's window scroll listener → ResizeObserver
                //      recomputes → portal rects churn during the exact frame React was inserting
                //      the new button, producing a visible flash.
                //   2. The `extras.animate()` outline hint fired on `[data-wb-extras="1"]` even
                //      when the button was appended via Tier 1 / Tier 2 (into `content.buttons`
                //      or `_style.buttons`), where the button doesn't live in the extras zone.
                //      Result: the outline flashed AROUND THE OLD EXTRAS AREA — visually
                //      unrelated to where the new button appeared.
                // Fix: no scroll, no outline flash. The button appears in-place under the
                // section content instantly. The success toast ("Button added — right under
                // the text") is enough visual acknowledgement.
                setAddBlockTarget(null);
              }}
              anchor="bottom"
            />
      )}

      {/* IMAGE EDITOR PANEL — opens on double-click of any image */}
      {imageEditorBlock && (() => {
        const blk = blocks.find(b => b.id === imageEditorBlock.blockId);
        if (!blk) return null;
        return (
          <ImageEditorPanel
            block={blk}
            field={imageEditorBlock.field}
            imageUrl={imageEditorBlock.imageUrl || blk.content?.image_url}
            onUpdate={updateBlock}
            onClose={() => setImageEditorBlock(null)}
            onMediaPick={() => openMediaPicker(imageEditorBlock.blockId, imageEditorBlock.field)}
          />
        );
      })()}

      {/* INLINE BUTTON EDITOR POPOVER — click any rendered button to edit. */}
      <InlineButtonEditor
        state={inlineBtn.editorState}
        button={inlineBtn.currentButton}
        onApply={inlineBtn.applyEdit}
        onRemove={inlineBtn.removeButton}
        onClose={inlineBtn.closeEditor}
      />

      {/* ── Floating Text Toolbar (Iter 154) ───────────────────────
          Anchors above the focused InlineText. Writes per-field typography
          into block.content._field_styles[field]. */}
      {editingText && (() => {
        const blk = sortedBlocks.find(b => b.id === editingText.blockId);
        if (!blk) return null;
        const styles = (blk.content || {})._field_styles || {};
        return (
          <TextToolbar
            rect={editingText.rect}
            style={styles[editingText.field]}
            onChange={(next) => updateBlock(blk.id, '_field_styles', { ...styles, [editingText.field]: next })}
            onClose={() => setEditingText(null)}
          />
        );
      })()}

      {/* ── Media Picker ── */}
      <MediaPickerModal open={showMediaPicker} onClose={() => setShowMediaPicker(false)} onSelect={handleMediaSelect} />

      {/* ── Fluid overlay layer (Iter 156) — portal-rendered grid + resize handles ── */}
      <FluidOverlayLayer
        canvasRef={canvasRef}
        blocks={sortedBlocks}
        onStartResize={(e, blockId) => fluidGrid?.startResize?.(e, blockId)}
      />
    </div>
    </BuilderDeviceContext.Provider>
  );
}
